import React, { useState, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  TextField,
  Grid,
  Divider,
  IconButton,
  Menu,
  useMediaQuery,
  alpha,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import GroupsIcon from '@mui/icons-material/GroupsRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import SendIcon from '@mui/icons-material/SendRounded';
import LinkIcon from '@mui/icons-material/LinkRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import ReportProgressOverlay, { ReportProgressState } from './ReportProgressOverlay';
import { ReconnectingWebSocket } from '../../socket';
import { SurfaceCard } from '../ui/SurfaceCard';
import { tokens } from '../../theme';
import { revealElement } from '../../utils/revealElement';

// App palette only (the generated report e-mails keep their own styling).
const ACCENT = tokens.gold;
const ACCENT_LIGHT = tokens.goldLight;

/** Field-group title. */
const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography component="h3" sx={{ fontSize: '0.875rem', fontWeight: 650, letterSpacing: '-0.005em', mb: 1.5 }}>
    {children}
  </Typography>
);

interface ManualReportPanelProps {
  onSend: (introText: string, recipients: string[], groupName?: string, startDate?: Date, endDate?: Date, reportType?: string) => void;
  socket: ReconnectingWebSocket;
}

interface User {
  username: string;
  email: string;
  status?: string;
  enabled?: boolean;
}

const DEFAULT_INTRO =
  "Welcome to your latest Liquidity Provider Report. This summary highlights your portfolio's recent performance, key metrics, and notable events. For a detailed breakdown and actionable insights, please review the full report. Thank you for trusting TeksCapital with your investment journey.";

const ManualReportPanel: React.FC<ManualReportPanelProps> = ({ onSend, socket }) => {
  const isPhone = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const [introText, setIntroText] = useState(DEFAULT_INTRO);
  const [groups, setGroups] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [groupUsers, setGroupUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [manualEmails, setManualEmails] = useState('');

  const [reportType, setReportType] = useState('full');
  const [reportTypes] = useState(['full', 'eth', 'fet', 'gno', 'qnt', 'rsk', 'weth']);

  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Report progress state
  const [reportProgress, setReportProgress] = useState<ReportProgressState>({
    active: false,
    step: '',
    current: 0,
    total: 100,
  });

  // Listen to socket events for report progress
  const handleReportProgress = useCallback((payload: any) => {
    setReportProgress({
      active: true,
      step: payload.step,
      current: payload.current,
      total: payload.total,
    });
  }, []);

  const handleReportComplete = useCallback((payload: any) => {
    setSending(false);
    setReportProgress(prev => ({
      ...prev,
      active: true,
      step: payload.success
        ? `Done! ${payload.sent} report${payload.sent !== 1 ? 's' : ''} sent.`
        : `Failed: ${payload.error || 'Unknown error'}`,
      current: 100,
      total: 100,
      result: {
        success: payload.success,
        sent: payload.sent,
        skipped: payload.skipped,
        total: payload.total,
        error: payload.error,
      },
    }));
  }, []);

  useEffect(() => {
    socket.on('reportProgress', handleReportProgress);
    socket.on('reportComplete', handleReportComplete);
    return () => {
      socket.off('reportProgress', handleReportProgress);
      socket.off('reportComplete', handleReportComplete);
    };
  }, [socket, handleReportProgress, handleReportComplete]);

  const dismissProgress = useCallback(() => {
    setReportProgress({ active: false, step: '', current: 0, total: 100 });
  }, []);

  // Load groups on mount
  useEffect(() => {
    setLoadingGroups(true);
    fetch('/api/reports/groups')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const sorted = [...data].sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: 'base' })
          );
          setGroups(sorted);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoadingGroups(false));
  }, []);

  // Fetch users when group is selected
  useEffect(() => {
    if (selectedGroup) {
      setLoadingUsers(true);

      const fetchGroup = async (group: string) => {
        const res = await fetch(`/api/reports/groups/${group}/users`);
        if (!res.ok) throw new Error('Failed to fetch group users');
        return res.json();
      };

      Promise.all([
        fetchGroup(selectedGroup),
        selectedGroup.toLowerCase() !== 'admin'
          ? fetchGroup('admin')
          : Promise.resolve([]),
      ])
        .then(([targetGroupUsers, adminUsers]) => {
          const combined = [...targetGroupUsers];
          if (Array.isArray(adminUsers)) {
            adminUsers.forEach((admin: User) => {
              if (!combined.some((u) => u.username === admin.username)) {
                combined.push(admin);
              }
            });
          }
          if (Array.isArray(combined)) {
            combined.sort((a, b) =>
              a.email.localeCompare(b.email, undefined, { sensitivity: 'base' })
            );
            setGroupUsers(combined);
            setSelectedUsers([]);
          }
        })
        .catch((err) => console.error(err))
        .finally(() => setLoadingUsers(false));
    } else {
      setGroupUsers([]);
      setSelectedUsers([]);
    }
  }, [selectedGroup]);

  // Sync Report Type -> Group
  useEffect(() => {
    if (!groups.length) return;
    if (reportType.toLowerCase() === 'full') {
      const adminGroup = groups.find((g) => g.toLowerCase() === 'admin');
      if (adminGroup && adminGroup !== selectedGroup) {
        setSelectedGroup(adminGroup);
      }
      return;
    }
    const match = groups.find((g) => g.toLowerCase() === reportType.toLowerCase());
    if (match && match !== selectedGroup) {
      setSelectedGroup(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, groups]);

  // Sync Group -> Report Type
  useEffect(() => {
    if (!selectedGroup) return;
    const target = selectedGroup.toLowerCase();
    if (reportTypes.includes(target) && target !== reportType) {
      setReportType(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  const noRecipients = selectedUsers.length === 0 && manualEmails.trim().length === 0;

  const handleSend = () => {
    const extraRecipients = manualEmails
      .split(/[,\s]+/)
      .map((e) => e.trim())
      .filter((e) => e.length > 0 && e.includes('@'));

    const finalRecipients = Array.from(
      new Set([...selectedUsers, ...extraRecipients])
    );

    if (finalRecipients.length === 0) {
      alert('Please select users or add manual recipients.');
      return;
    }

    setSending(true);
    setReportProgress({
      active: true,
      step: 'Starting report generation...',
      current: 0,
      total: 100,
    });

    onSend(introText, finalRecipients, selectedGroup, undefined, undefined, reportType);
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === groupUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(groupUsers.map((u) => u.email));
    }
  };

  const handleToggleUser = (email: string) => {
    const currentIndex = selectedUsers.indexOf(email);
    const newSelected = [...selectedUsers];
    if (currentIndex === -1) {
      newSelected.push(email);
    } else {
      newSelected.splice(currentIndex, 1);
    }
    setSelectedUsers(newSelected);
  };

  const handlePreview = async (mode: 'teaser' | 'full') => {
    const newWindow = window.open('', '_blank');
    if (!newWindow) {
      alert('Please allow popups to see the preview');
      return;
    }

    const title = mode === 'teaser' ? 'Email Teaser' : 'Full Report';
    newWindow.document.write(
      `<html><head><title>Generating ${title}...</title><style>body{font-family:sans-serif;background:#1a1a1a;color:#fff;display:flex;justify-content:center;align-items:center;height:100vh;margin:0;}</style></head><body><h2>Generating ${title} Preview...</h2></body></html>`
    );

    try {
      setPreviewLoading(true);
      const response = await fetch('/api/reports/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          introText,
          groupName: selectedGroup,
          reportType,
          previewMode: mode,
        }),
      });

      if (!response.ok) throw new Error('Preview failed');
      const html = await response.text();
      newWindow.document.open();
      newWindow.document.write(html);
      newWindow.document.close();
    } catch (error) {
      console.error('Preview error:', error);
      if (newWindow) {
        newWindow.document.body.innerHTML =
          '<h2 style="color:red">Failed to generate preview</h2>';
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleShareLink = async () => {
    try {
      setLinkLoading(true);
      const response = await fetch('/api/reports/generate-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          introText,
          groupName: selectedGroup,
          reportType,
        }),
      });

      if (!response.ok) throw new Error('Link generation failed');
      const data = await response.json();

      if (data.success && data.url) {
        await navigator.clipboard.writeText(data.url);
        window.open(data.url, '_blank');
      } else {
        alert('Failed to generate report link');
      }
    } catch (error) {
      console.error('Share link error:', error);
      alert('Failed to generate report link');
    } finally {
      setLinkLoading(false);
    }
  };

  // Report accents (crimson / mint / orchid) stay as small identity marks on glass.
  const fieldSx = {
    '& .MuiOutlinedInput-root': { bgcolor: tokens.glass.inset },
  };

  return (
    <SurfaceCard padding="none" radius="lg" tint={ACCENT} sx={{ position: 'relative' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, px: { xs: 2, sm: 3 }, pt: { xs: 2.25, sm: 3 }, pb: { xs: 2, sm: 2.5 } }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '14px',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            color: ACCENT_LIGHT,
            bgcolor: alpha(ACCENT, 0.14),
            border: `1px solid ${alpha(ACCENT, 0.3)}`,
          }}
        >
          <SendIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: { xs: '1.0625rem', sm: '1.1875rem' }, fontWeight: 700, letterSpacing: '-0.01em' }}>
            Send report
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', mt: 0.25 }}>
            Compose and deliver performance reports to stakeholders
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: tokens.borderSubtle }} />

      {/* Content */}
      <Grid container sx={{ minHeight: { md: 500 } }}>
        {/* Left Column: Intro Text */}
        <Grid
          size={{ xs: 12, md: 7 }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: { xs: 2, sm: 3 },
            borderRight: { md: `1px solid ${tokens.borderSubtle}` },
            borderBottom: { xs: `1px solid ${tokens.borderSubtle}`, md: 'none' },
          }}
        >
          <FieldLabel>Executive summary</FieldLabel>
          <Box
            sx={{
              mb: 3,
              flex: 1,
              minHeight: 280,
              '& .quill': {
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              },
              '& .ql-container': {
                flex: 1,
                borderBottomLeftRadius: 16,
                borderBottomRightRadius: 16,
                fontFamily: 'inherit',
                fontSize: '0.9375rem',
                '@media (pointer: coarse)': { fontSize: 16 },
                bgcolor: tokens.glass.inset,
                color: '#FFFFFF',
                borderColor: tokens.border,
                borderTop: 'none',
                '& .ql-editor': {
                  color: '#FFFFFF',
                  lineHeight: 1.6,
                  '&::before': {
                    color: 'rgba(255,255,255,0.4)',
                    fontStyle: 'normal',
                  },
                },
              },
              '& .ql-toolbar': {
                // One row: compact groups; the heading picker is dropped on phones to make room.
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                rowGap: 0.5,
                '& .ql-formats': { display: 'inline-flex', alignItems: 'center', mr: '6px !important' },
                '& button': { width: 32, height: 32, p: '6px', borderRadius: '8px' },
                '& button:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                // Heading picker: same 32px row as the buttons, a single chevron instead of Quill's
                // up/down arrows.
                '& .ql-picker.ql-header': { width: 112, height: 32 },
                '& .ql-picker-label': {
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: '100%',
                  pl: 1,
                  pr: 3,
                  border: 'none !important',
                  borderRadius: '8px',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', color: ACCENT_LIGHT },
                  '& svg': { display: 'none' },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    width: 6,
                    height: 6,
                    borderRight: '1.5px solid currentColor',
                    borderBottom: '1.5px solid currentColor',
                    transform: 'translateY(-70%) rotate(45deg)',
                  },
                },
                '& .ql-picker.ql-expanded .ql-picker-label': { color: ACCENT_LIGHT },
                '& .ql-picker-item': { fontSize: '0.875rem' },
                '@media (pointer: coarse)': {
                  '& button': { width: 44, height: 44, p: '12px' },
                  '& .ql-picker.ql-header': { height: 44 },
                },
                // Phones: drop the heading picker and "clear formatting" so one row of 44px buttons fits.
                '@media (max-width: 599.95px)': {
                  '& .ql-formats:has(.ql-header)': { display: 'none' },
                  '& .ql-formats:has(.ql-clean)': { display: 'none' },
                },
                // Touch tablets: 44px buttons don't leave room for "clear formatting" either; never wrap
                // to a second row (scroll sideways as a last resort).
                '@media (pointer: coarse) and (max-width: 1279.95px)': {
                  flexWrap: 'nowrap',
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                  '& .ql-formats:has(.ql-clean)': { display: 'none' },
                },
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                bgcolor: 'rgba(255,255,255,0.05)',
                borderColor: tokens.border,
                fontFamily: 'inherit',
                '& .ql-stroke': { stroke: tokens.textSecondary },
                '& .ql-fill': { fill: tokens.textSecondary },
                '& .ql-picker': { color: tokens.textSecondary },
                '& .ql-picker-options': { bgcolor: 'rgba(24,24,27,0.96)', borderColor: tokens.border, borderRadius: '10px' },
                '& button:hover .ql-stroke': { stroke: ACCENT_LIGHT },
                '& button:hover .ql-fill': { fill: ACCENT_LIGHT },
                '& button.ql-active .ql-stroke': { stroke: ACCENT_LIGHT },
                '& button.ql-active .ql-fill': { fill: ACCENT_LIGHT },
              },
            }}
          >
            <ReactQuill
              theme="snow"
              value={introText}
              onChange={setIntroText}
              placeholder="Write your executive summary here..."
              style={{ height: '100%' }}
            />
          </Box>

          <FieldLabel>Report configuration</FieldLabel>
          <FormControl fullWidth size="small" sx={fieldSx}>
            <InputLabel>Report type</InputLabel>
            <Select
              value={reportType}
              label="Report type"
              onChange={(e) => setReportType(e.target.value)}
            >
              {reportTypes.map((t) => (
                <MenuItem key={t} value={t}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: t === 'full' ? ACCENT : 'rgba(255,255,255,0.5)',
                      }}
                    />
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Right Column: Audience Selection */}
        <Grid
          size={{ xs: 12, md: 5 }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: { xs: 2, sm: 3 },
            bgcolor: 'rgba(0,0,0,0.14)',
          }}
        >
          <Box id="report-audience" sx={{ scrollMarginTop: 96, borderRadius: '12px' }}>
            <FieldLabel>Target audience</FieldLabel>
          </Box>

          <FormControl fullWidth sx={{ mb: 2, ...fieldSx }}>
            <InputLabel id="group-select-label">Cognito group</InputLabel>
            <Select
              labelId="group-select-label"
              value={selectedGroup}
              label="Cognito group"
              onChange={(e) => setSelectedGroup(e.target.value)}
              startAdornment={<GroupsIcon sx={{ mr: 1, color: 'text.secondary' }} />}
            >
              {loadingGroups ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                groups.map((group) => (
                  <MenuItem key={group} value={group}>
                    {group}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {selectedGroup && (
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                mb: 2,
                maxHeight: 320,
                borderRadius: '16px',
                bgcolor: tokens.glass.inset,
                border: `1px solid ${tokens.borderSubtle}`,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  py: 1,
                  pl: 1.5,
                  pr: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: `1px solid ${tokens.borderSubtle}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      minWidth: 24,
                      height: 22,
                      px: 0.75,
                      display: 'inline-grid',
                      placeItems: 'center',
                      borderRadius: 999,
                      bgcolor: 'rgba(255,255,255,0.08)',
                      border: `1px solid ${tokens.border}`,
                      color: 'text.primary',
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {selectedUsers.length}
                  </Box>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
                    Members selected
                  </Typography>
                </Box>
                <Button size="small" onClick={handleSelectAll} sx={{ color: ACCENT_LIGHT, '&:hover': { bgcolor: alpha(ACCENT, 0.1) } }}>
                  {selectedUsers.length === groupUsers.length
                    ? 'Unselect all'
                    : 'Select all'}
                </Button>
              </Box>
              <List
                dense
                sx={{
                  flexGrow: 1,
                  bgcolor: 'transparent',
                  p: 0,
                  overflowY: 'auto',
                }}
              >
                {loadingUsers ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress size={28} sx={{ color: ACCENT_LIGHT }} />
                  </Box>
                ) : (
                  groupUsers.map((user) => {
                    const labelId = `checkbox-list-label-${user.username}`;
                    const isSelected = selectedUsers.indexOf(user.email) !== -1;
                    return (
                      <ListItem
                        key={user.username}
                        disablePadding
                        sx={{
                          borderBottom: `1px solid ${tokens.borderSubtle}`,
                          '&:last-child': { borderBottom: 'none' },
                        }}
                      >
                        <ListItemButton
                          role={undefined}
                          onClick={() => handleToggleUser(user.email)}
                          dense
                          sx={{ py: 1.25, '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' } }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Checkbox
                              edge="start"
                              checked={isSelected}
                              tabIndex={-1}
                              disableRipple
                              inputProps={{ 'aria-labelledby': labelId }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            id={labelId}
                            primary={user.email}
                            secondary={
                              user.username !== user.email
                                ? user.username
                                : null
                            }
                            primaryTypographyProps={{
                              noWrap: true,
                              sx: {
                                fontSize: '0.875rem',
                                color: isSelected ? 'text.primary' : 'text.secondary',
                                fontWeight: isSelected ? 600 : 500,
                              },
                            }}
                            secondaryTypographyProps={{
                              noWrap: true,
                              sx: { color: 'text.disabled', fontSize: '0.75rem' },
                            }}
                          />
                          {!user.enabled && (
                            <Chip
                              size="small"
                              label="Disabled"
                              sx={{
                                height: 22,
                                fontSize: '0.6875rem',
                                fontWeight: 650,
                                bgcolor: alpha('#FF7070', 0.14),
                                color: '#FF7070',
                                border: `1px solid ${alpha('#FF7070', 0.3)}`,
                              }}
                            />
                          )}
                        </ListItemButton>
                      </ListItem>
                    );
                  })
                )}
                {groupUsers.length === 0 && !loadingUsers && (
                  <Box p={3} textAlign="center">
                    <Typography variant="body2" color="text.secondary">
                      No users found in this group.
                    </Typography>
                  </Box>
                )}
              </List>
            </Box>
          )}

          <Box sx={{ mt: 'auto' }}>
            <FieldLabel>Additional recipients</FieldLabel>
            <TextField
              placeholder="email1@example.com, email2@example.com"
              value={manualEmails}
              onChange={(e) => setManualEmails(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={2}
              helperText="Separate multiple emails with commas or spaces"
              sx={{ ...fieldSx, '& .MuiFormHelperText-root': { mx: 0, mt: 1 } }}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Footer actions — phones: one sticky row above the tab bar (icon previews + send) */}
      {isPhone ? (
        <Box
          sx={{
            // Sits right on top of the floating tab bar, one shared surface; content fades out above it.
            position: 'sticky',
            bottom: 'calc(84px + env(safe-area-inset-bottom))',
            zIndex: 2,
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'rgba(14,14,16,0.95)',
            backdropFilter: 'blur(24px) saturate(150%)',
            WebkitBackdropFilter: 'blur(24px) saturate(150%)',
            borderTop: `1px solid ${tokens.borderSubtle}`,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: '100%',
              height: 24,
              pointerEvents: 'none',
              background: 'linear-gradient(180deg, rgba(14,14,16,0) 0%, rgba(14,14,16,0.95) 100%)',
            },
            // The same surface continues behind the tab bar down to the screen edge, so no form
            // content shows between the action row and the tab bar.
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 0,
              right: 0,
              top: '100%',
              height: 'calc(84px + env(safe-area-inset-bottom))',
              pointerEvents: 'none',
              bgcolor: 'rgba(14,14,16,0.95)',
              backdropFilter: 'blur(24px) saturate(150%)',
              WebkitBackdropFilter: 'blur(24px) saturate(150%)',
            },
          }}
        >
          <IconButton
            aria-label="More report actions"
            aria-haspopup="menu"
            onClick={(e) => setMoreAnchor(e.currentTarget)}
            sx={{ width: 44, height: 44, border: `1px solid ${tokens.border}`, color: 'text.secondary' }}
          >
            {previewLoading || linkLoading ? <CircularProgress size={18} /> : <MoreHorizRoundedIcon />}
          </IconButton>
          <Menu
            anchorEl={moreAnchor}
            open={Boolean(moreAnchor)}
            onClose={() => setMoreAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            slotProps={{ paper: { sx: { mt: -1, minWidth: 220 } } }}
          >
            <MenuItem onClick={() => { setMoreAnchor(null); handlePreview('teaser'); }} disabled={previewLoading}>
              <ListItemIcon><EmailRoundedIcon fontSize="small" /></ListItemIcon>
              Preview email
            </MenuItem>
            <MenuItem onClick={() => { setMoreAnchor(null); handlePreview('full'); }} disabled={previewLoading}>
              <ListItemIcon><DescriptionRoundedIcon fontSize="small" /></ListItemIcon>
              Preview report
            </MenuItem>
            <MenuItem onClick={() => { setMoreAnchor(null); handleShareLink(); }} disabled={linkLoading}>
              <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
              Copy share link
            </MenuItem>
          </Menu>
          {noRecipients && !sending ? (
            // Next step instead of a dead button: take the user to the audience picker. It is the only
            // action here, so it gets the primary (gold) style.
            <Button
              key="choose"
              onClick={() => revealElement('report-audience')}
              variant="contained"
              color="secondary"
              startIcon={<GroupAddRoundedIcon />}
              sx={{ flex: 1, whiteSpace: 'nowrap' }}
            >
              Choose recipients
            </Button>
          ) : (
            <Button
              key="send"
              onClick={handleSend}
              variant="contained"
              color="secondary"
              disabled={sending}
              startIcon={sending ? <CircularProgress size={16} sx={{ color: 'rgba(0,0,0,0.6)' }} /> : <SendIcon />}
              sx={{ flex: 1, whiteSpace: 'nowrap' }}
            >
              {sending ? 'Sending…' : 'Send report'}
            </Button>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1.5,
            borderTop: `1px solid ${tokens.borderSubtle}`,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              onClick={() => handlePreview('teaser')}
              variant="outlined"
              startIcon={previewLoading ? <CircularProgress size={16} /> : <EmailRoundedIcon />}
              disabled={previewLoading}
            >
              Preview email
            </Button>
            <Button
              onClick={() => handlePreview('full')}
              variant="outlined"
              startIcon={previewLoading ? <CircularProgress size={16} /> : <DescriptionRoundedIcon />}
              disabled={previewLoading}
            >
              Preview report
            </Button>
            <Button
              onClick={handleShareLink}
              variant="outlined"
              startIcon={linkLoading ? <CircularProgress size={16} /> : <LinkIcon />}
              disabled={linkLoading}
            >
              {linkLoading ? 'Generating…' : 'Share link'}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            {noRecipients && !sending && (
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                Select at least one recipient
              </Typography>
            )}
            <Button
              onClick={handleSend}
              variant="contained"
              color="secondary"
              disabled={noRecipients || sending}
              startIcon={sending ? <CircularProgress size={16} sx={{ color: 'rgba(0,0,0,0.6)' }} /> : <SendIcon />}
              sx={{ px: 3.5 }}
            >
              {sending ? 'Sending…' : 'Send report'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Report generation progress overlay */}
      <ReportProgressOverlay progress={reportProgress} onDismiss={dismissProgress} />
    </SurfaceCard>
  );
};

export default ManualReportPanel;
